import React from 'react';
import { useAuth } from './AuthProvider';
import SupportPageLink from './SupportPageLink';
import './LoginPage.css';

// The login page renders at whatever URL was asked for, so a link into the app (the public support page's
// Agent Hotline button, /my-tickets?chat=hotline) survives sign-in: the callbacks return to what is
// recorded here instead of Home. Keyed per provider, the keys the callbacks already read.
function rememberReturnTo(key: 'msalReturnTo' | 'oktaReturnTo'): void {
  const path = window.location.pathname + window.location.search;
  if (path === '/') return;
  try {
    sessionStorage.setItem(key, path);
  } catch { /* sessionStorage unavailable: sign-in lands on Home */ }
}

const LoginPage: React.FC = () => {
  const { loginWithMicrosoft, loginWithOkta } = useAuth();

  // The Okta SP-initiated button shows everywhere EXCEPT the production site. In
  // prod, Okta users arrive via the IdP-initiated dashboard tile, not this button;
  // staging, per-branch dev deploys, and local development all use it for testing.
  const PROD_HOSTNAMES = ['os.weinsureagent.com'];
  const isProd = PROD_HOSTNAMES.includes(window.location.hostname);
  const showOktaButton = !isProd;

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/assets/we_insure_agencyos_vertical.png" alt="We Insure AgencyOS" className="login-logo" />
        </div>

        <div className="login-buttons">
          <button onClick={() => { rememberReturnTo('msalReturnTo'); loginWithMicrosoft(); }} className="login-button microsoft-button login-button-primary" data-ph-capture-attribute-auth-provider="microsoft">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M0 0h11.377v11.372H0zm12.623 0H24v11.372H12.623zM0 12.623h11.377V24H0zm12.623 0H24V24H12.623z"/>
            </svg>
            <span>Sign in with Microsoft</span>
          </button>
          {showOktaButton && (
            <button onClick={() => { rememberReturnTo('oktaReturnTo'); loginWithOkta(); }} className="login-button okta-button" data-ph-capture-attribute-auth-provider="okta">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.385 0 0 5.385 0 12s5.385 12 12 12 12-5.385 12-12S18.615 0 12 0zm0 18a6 6 0 110-12 6 6 0 010 12z"/>
              </svg>
              <span>Sign in with Okta</span>
            </button>
          )}
        </div>

        {/* The support page opens without signing in, for anyone stuck here. */}
        <SupportPageLink className="login-support-tile">
          <span className="login-support-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 18h2v-2h-2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8m0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4"/>
            </svg>
          </span>
          <span className="login-support-text">
            <span className="login-support-title">Need help?</span>
            <span className="login-support-subtitle">Contact your support team</span>
          </span>
          <svg className="login-support-arrow" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </SupportPageLink>

        <div className="login-footer">
          <p>Secure access for authorized users only</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
